// src/pages/Admin/Components/EditProductPage.jsx
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  X,
  Image as ImageIcon,
  UploadCloud,
  Trash2,
  CheckCircle,
  Save,
  Ruler,
  Maximize2,
  Loader2,
  Plus,
  Minus,
  RefreshCw
} from 'lucide-react';
import { adminUpdateProduct, adminCreateColor } from '../../../services/adminServices';
import { backend } from '../../../utils/axiosinstance';
import { getCategories } from '../../../services/categoryServices';

const initialFormData = {
  name: '',
  price: '',
  weight: '',
  warranty: '',
  brand: '',
  category: '',
  status: '',
  color: [],
  width: '',
  length: '',
  height: '',
  description: '',
  countInStock: 0,
  // 🛒 فیلدهای خرید عمده
  wholesale_enabled: false,
  wholesale_min_quantity: '',
  wholesale_price: '',
};

const inputClassName =
  'w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-500/10';

const selectClassName =
  'w-full cursor-pointer rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-500/10';

const FormInput = ({ label, name, value, onChange, type = 'text', placeholder, isSelect = false, options = [], multiple = false, disabled = false }) => {
  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={name} className="text-sm font-bold text-slate-700">{label}</label>
      {isSelect ? (
        <select
          id={name}
          name={name}
          value={value}
          onChange={onChange}
          className={selectClassName}
          multiple={multiple}
          style={multiple ? { height: '100px' } : {}}
          disabled={disabled}
        >
          <option value="">انتخاب کنید</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      ) : (
        <input
          id={name}
          name={name}
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className={inputClassName}
          disabled={disabled}
        />
      )}
    </div>
  );
};

const ImageSlot = ({ image, label, onUpload, onRemove, isMain = false, onImageClick }) => {
  return (
    <div className={`relative overflow-hidden rounded-2xl border ${isMain ? 'h-[220px] w-[220px]' : 'h-[105px] w-[105px]'} border-slate-200 bg-slate-50 group`}>
      {image ? (
        <>
          <img src={image} alt={label} className="h-full w-full object-cover cursor-pointer" onClick={() => onImageClick(image)} />
          <div className="absolute right-2 top-2 flex gap-2">
            <button type="button" onClick={() => onImageClick(image)} aria-label="بزرگنمایی" className="rounded-full bg-white/95 p-1.5 text-slate-600 shadow-md transition hover:scale-110 hover:bg-blue-50">
              <Maximize2 className="h-4 w-4" />
            </button>
            <button type="button" onClick={onRemove} aria-label={`حذف ${label}`} className="rounded-full bg-white/95 p-1.5 text-red-500 shadow-md transition hover:scale-110 hover:bg-red-50">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </>
      ) : (
        <label className="flex h-full w-full cursor-pointer flex-col items-center justify-center px-2 text-center transition hover:bg-blue-50">
          <UploadCloud className={`mb-2 ${isMain ? 'h-8 w-8' : 'h-6 w-6'} text-blue-400`} />
          <span className={`font-bold text-slate-600 ${isMain ? 'text-sm' : 'text-[10px]'}`}>{label}</span>
          {isMain && <span className="mt-1 text-[10px] text-slate-400">حداکثر ۵ مگابایت</span>}
          <input type="file" accept="image/*" onChange={onUpload} className="hidden" />
        </label>
      )}
    </div>
  );
};

const SectionTitle = ({ icon: Icon, children }) => {
  return (
    <div className="mb-6 flex items-center gap-3 border-b border-slate-100 pb-4">
      <div className="rounded-xl bg-blue-50 p-2 text-blue-600"><Icon className="h-5 w-5" /></div>
      <h2 className="text-lg font-extrabold text-slate-800">{children}</h2>
    </div>
  );
};

function dataURLtoBlob(dataURL) {
  const arr = dataURL.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) { u8arr[n] = bstr.charCodeAt(n); }
  return new Blob([u8arr], { type: mime });
}

const ColorStockInput = ({ colorName, colorCode, stock, onStockChange, onRemove }) => {
  return (
    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
      <div
        className="w-6 h-6 rounded-full border border-gray-300 flex-shrink-0"
        style={{ backgroundColor: colorCode || '#ccc' }}
      />
      <span className="text-sm font-medium text-gray-700 flex-1">{colorName}</span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onStockChange(Math.max(0, stock - 1))}
          className="w-7 h-7 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center transition"
        >
          <Minus className="w-3.5 h-3.5 text-gray-600" />
        </button>
        <input
          type="number"
          min="0"
          value={stock}
          onChange={(e) => onStockChange(parseInt(e.target.value) || 0)}
          className="w-16 text-center border border-gray-300 rounded-lg py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
        <button
          type="button"
          onClick={() => onStockChange(stock + 1)}
          className="w-7 h-7 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center transition"
        >
          <Plus className="w-3.5 h-3.5 text-gray-600" />
        </button>
      </div>
      <button
        type="button"
        onClick={onRemove}
        className="text-red-400 hover:text-red-600 transition p-1"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

const EditProductPage = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.auth) || {};
  const [loadingProduct, setLoadingProduct] = useState(true);

  const [formData, setFormData] = useState(initialFormData);
  const [mainImage, setMainImage] = useState(null);
  const [galleryImages, setGalleryImages] = useState([null, null, null]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [uploadError, setUploadError] = useState('');
  const [colorsList, setColorsList] = useState([]);
  const [loadingColors, setLoadingColors] = useState(true);

  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);

  const [colorStocks, setColorStocks] = useState({});
  const [totalStock, setTotalStock] = useState(0);

  const [successMessage, setSuccessMessage] = useState('');

  const [showColorModal, setShowColorModal] = useState(false);
  const [newColorName, setNewColorName] = useState('');
  const [newColorCode, setNewColorCode] = useState('');
  const [colorModalError, setColorModalError] = useState('');
  const [submittingColor, setSubmittingColor] = useState(false);

  const fetchCategories = async () => {
    setLoadingCategories(true);
    try {
      const res = await getCategories({ userInfo, dispatch });
      setCategories(res.data);
    } catch (err) {
      console.error('خطا در دریافت دسته‌بندی‌ها:', err);
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchColors = () => {
    setLoadingColors(true);
    backend.get('/api/v1/products/colors/')
      .then(res => {
        setColorsList(res.data);
        setLoadingColors(false);
      })
      .catch(err => {
        console.error('خطا در دریافت رنگ‌ها:', err);
        setLoadingColors(false);
      });
  };

  const fetchProduct = () => {
    setLoadingProduct(true);
    backend.get(`/api/v1/products/${id}/`)
      .then(res => {
        const product = res.data;
        setFormData({
          name: product.name || '',
          price: product.price || '',
          weight: product.weight || '',
          warranty: product.guarantee || '',
          brand: product.material || '',
          category: product.category || '',
          status: product.countInStock > 0 ? (product.countInStock > 10 ? 'active' : 'almost-finished') : 'inactive',
          color: product.colors?.map(c => c.name) || [],
          width: product.dimensions?.width || '',
          length: product.dimensions?.depth || '',
          height: product.dimensions?.height || '',
          description: product.description || '',
          countInStock: product.countInStock || 0,
          // 🛒 فیلدهای خرید عمده
          wholesale_enabled: product.wholesale_enabled || false,
          wholesale_min_quantity: product.wholesale_min_quantity || '',
          wholesale_price: product.wholesale_price || '',
        });

        const stocks = {};
        (product.color_stocks || []).forEach(cs => {
          const colorName = cs.color_name || colorsList.find(c => c.id === cs.color)?.name;
          if (colorName) {
            stocks[colorName] = cs.stock || 0;
          }
        });
        setColorStocks(stocks);

        if (product.image) setMainImage(product.image);
        const gallery = product.gallery || [];
        const newGallery = [null, null, null];
        gallery.forEach((url, index) => {
          if (index < 3) newGallery[index] = url;
        });
        setGalleryImages(newGallery);
        setLoadingProduct(false);
      })
      .catch(err => {
        console.error('خطا در دریافت محصول:', err);
        setUploadError('خطا در دریافت اطلاعات محصول');
        setLoadingProduct(false);
      });
  };

  useEffect(() => {
    fetchCategories();
    fetchColors();
    fetchProduct();
  }, [id]);

  useEffect(() => {
    const total = Object.values(colorStocks).reduce((sum, val) => sum + (parseInt(val) || 0), 0);
    setTotalStock(total);
    setFormData(prev => ({ ...prev, countInStock: total }));
  }, [colorStocks]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleColorSelect = (event) => {
    const options = event.target.options;
    const selected = [];
    for (let i = 0; i < options.length; i++) {
      if (options[i].selected) selected.push(options[i].value);
    }
    setFormData(prev => ({ ...prev, color: selected }));

    const newStocks = { ...colorStocks };
    selected.forEach(name => {
      if (!(name in newStocks)) {
        newStocks[name] = 0;
      }
    });
    Object.keys(newStocks).forEach(name => {
      if (!selected.includes(name)) {
        delete newStocks[name];
      }
    });
    setColorStocks(newStocks);
  };

  const handleStockChange = (colorName, value) => {
    setColorStocks(prev => ({ ...prev, [colorName]: parseInt(value) || 0 }));
  };

  const handleRemoveColor = (colorName) => {
    const newColors = formData.color.filter(c => c !== colorName);
    setFormData(prev => ({ ...prev, color: newColors }));
    const newStocks = { ...colorStocks };
    delete newStocks[colorName];
    setColorStocks(newStocks);
  };

  const handleAddColor = () => {
    if (!newColorName.trim() || !newColorCode.trim()) {
      setColorModalError('لطفاً نام و کد رنگ را وارد کنید.');
      return;
    }
    if (!/^#[0-9A-F]{6}$/i.test(newColorCode)) {
      setColorModalError('کد رنگ باید به فرمت #XXXXXX باشد.');
      return;
    }
    setSubmittingColor(true);
    adminCreateColor({ name: newColorName, code: newColorCode }, { userInfo, dispatch })
      .then(() => {
        setNewColorName('');
        setNewColorCode('');
        setColorModalError('');
        setShowColorModal(false);
        fetchColors();
      })
      .catch(err => {
        setColorModalError(err.response?.data?.detail || 'خطا در ایجاد رنگ.');
      })
      .finally(() => setSubmittingColor(false));
  };

  const readImageFile = (file, callback) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('لطفاً فقط فایل تصویری انتخاب کنید.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('حجم تصویر نباید بیشتر از ۵ مگابایت باشد.');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => { callback(reader.result); };
    reader.readAsDataURL(file);
  };

  const handleMainImageUpload = (event) => {
    readImageFile(event.target.files?.[0], setMainImage);
  };

  const handleGalleryUpload = (event, index) => {
    readImageFile(event.target.files?.[0], (image) => {
      setGalleryImages(prev => {
        const updated = [...prev];
        updated[index] = image;
        return updated;
      });
    });
  };

  const removeGalleryImage = (index) => {
    setGalleryImages(prev => {
      const updated = [...prev];
      updated[index] = null;
      return updated;
    });
  };

  const openLightbox = (imageSrc) => {
    if (imageSrc) setLightboxImage(imageSrc);
  };

  const closeLightbox = () => setLightboxImage(null);

  const handleToggleStock = async () => {
    const currentStock = formData.countInStock || 0;
    const newStock = currentStock > 0 ? 0 : 10;
    const newStatus = currentStock > 0 ? 'ناموجود' : 'موجود';

    if (!window.confirm(`آیا از ${currentStock > 0 ? 'ناموجود' : 'موجود'} کردن این محصول مطمئن هستید؟`)) return;

    try {
      await adminUpdateProduct(
        id,
        {
          stockStatus: newStatus,
          countInStock: newStock,
        },
        { userInfo, dispatch }
      );

      setSuccessMessage(`وضعیت محصول با موفقیت به "${newStatus}" تغییر یافت.`);
      setFormData(prev => ({
        ...prev,
        countInStock: newStock,
        status: newStock > 0 ? 'active' : 'inactive'
      }));
      setTotalStock(newStock);

      setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
    } catch (err) {
      alert('خطا در تغییر وضعیت موجودی: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setUploadError('');
    setSuccessMessage('');

    if (!formData.name.trim()) {
      alert('لطفاً نام محصول را وارد کنید.');
      return;
    }

    setIsSubmitting(true);

    try {
      const fd = new FormData();
      fd.append('name', formData.name);
      fd.append('price', formData.price || 0);
      fd.append('weight', formData.weight || 0);
      fd.append('guarantee', formData.warranty || '');
      fd.append('material', formData.brand || '');
      fd.append('category', formData.category || '');
      fd.append('description', formData.description || '');
      fd.append('countInStock', totalStock);
      fd.append('dimensions_width', formData.width || 0);
      fd.append('dimensions_depth', formData.length || 0);
      fd.append('dimensions_height', formData.height || 0);

      // 🛒 فیلدهای خرید عمده
      fd.append('wholesale_enabled', formData.wholesale_enabled);
      fd.append('wholesale_min_quantity', formData.wholesale_min_quantity || 0);
      fd.append('wholesale_price', formData.wholesale_price || 0);

      if (formData.color && formData.color.length > 0) {
        fd.append('colors', JSON.stringify(formData.color));
      } else {
        fd.append('colors', JSON.stringify([]));
      }

      const colorStocksData = Object.entries(colorStocks)
        .filter(([name, stock]) => stock > 0)
        .map(([name, stock]) => {
          const color = colorsList.find(c => c.name === name);
          return { color: color?.id, stock: parseInt(stock) || 0 };
        })
        .filter(item => item.color);

      fd.append('color_stocks', JSON.stringify(colorStocksData));

      if (mainImage === null) {
        fd.append('image', '');
      } else if (mainImage && mainImage.startsWith('data:image')) {
        const blob = dataURLtoBlob(mainImage);
        fd.append('image', blob, 'main.jpg');
      }

      const keepUrls = galleryImages
        .filter(img => img !== null && !img.startsWith('data:image'))
        .map(img => img);

      const newImages = galleryImages
        .filter(img => img !== null && img.startsWith('data:image'));

      fd.append('gallery_keep', JSON.stringify(keepUrls));

      for (let img of newImages) {
        const blobGallery = dataURLtoBlob(img);
        fd.append('gallery', blobGallery, `gallery_${Date.now()}.jpg`);
      }

      await adminUpdateProduct(id, fd, { userInfo, dispatch });
      setSuccessMessage('محصول با موفقیت به‌روزرسانی شد!');
      setTimeout(() => setSuccessMessage(''), 3000);
      navigate('/admin');
    } catch (error) {
      console.error('خطا در به‌روزرسانی محصول:', error);
      if (error.response?.data) {
        const serverErrors = error.response.data;
        let errorMessages = '';
        if (typeof serverErrors === 'object') {
          for (const [field, messages] of Object.entries(serverErrors)) {
            if (Array.isArray(messages)) {
              errorMessages += `${field}: ${messages.join(', ')}\n`;
            } else {
              errorMessages += `${field}: ${messages}\n`;
            }
          }
          setUploadError(errorMessages || JSON.stringify(serverErrors, null, 2));
        } else {
          setUploadError(serverErrors.detail || serverErrors.message || 'خطا در به‌روزرسانی محصول');
        }
      } else {
        setUploadError(error.message || 'خطا در به‌روزرسانی محصول');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loadingProduct) return <div className="text-center py-10">در حال بارگذاری محصول...</div>;

  return (
    <main dir="rtl" className="min-h-screen bg-slate-100 p-4 md:p-8">
      <div className="mx-auto max-w-5xl">
        <header className="mb-8">
          <div className="rounded-3xl bg-gradient-to-l from-blue-700 to-blue-500 px-6 py-8 text-white shadow-xl shadow-blue-200">
            <p className="mb-2 text-sm font-medium text-blue-100">مدیریت فروشگاه</p>
            <h1 className="text-2xl font-black md:text-3xl">ویرایش محصول</h1>
            <p className="mt-3 text-sm text-blue-100">اطلاعات محصول را ویرایش کنید.</p>
          </div>
        </header>

        {successMessage && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-xl text-green-700 text-sm">
            ✅ {successMessage}
          </div>
        )}
        {uploadError && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm whitespace-pre-line">
            ⚠️ {uploadError}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* بخش تصاویر */}
          <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm md:p-7">
            <div className="flex items-center justify-between">
              <SectionTitle icon={ImageIcon}>تصاویر محصول</SectionTitle>
              <button
                type="button"
                onClick={handleToggleStock}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold text-white transition-all ${
                  formData.countInStock > 0
                    ? 'bg-red-500 hover:bg-red-600'
                    : 'bg-green-500 hover:bg-green-600'
                }`}
              >
                <RefreshCw className="w-4 h-4" />
                {formData.countInStock > 0 ? 'ناموجود کردن محصول' : 'موجود کردن محصول'}
              </button>
            </div>
            <div className="flex flex-col items-center gap-6 md:flex-row md:items-start md:justify-center md:gap-10 mt-4">
              <div className="flex flex-col items-center gap-3">
                <ImageSlot image={mainImage} label="تصویر اصلی" isMain onUpload={handleMainImageUpload} onRemove={() => setMainImage(null)} onImageClick={openLightbox} />
                <span className="text-xs font-medium text-slate-400">تصویر اصلی محصول</span>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {galleryImages.map((image, index) => (
                  <ImageSlot key={index} image={image} label={`گالری ${index + 1}`} onUpload={(event) => handleGalleryUpload(event, index)} onRemove={() => removeGalleryImage(index)} onImageClick={openLightbox} />
                ))}
              </div>
            </div>
          </section>

          {/* بخش اطلاعات محصول */}
          <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm md:p-7">
            <SectionTitle icon={CheckCircle}>اطلاعات محصول</SectionTitle>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
              <FormInput label="نام محصول" name="name" value={formData.name} onChange={handleChange} placeholder="نام محصول را وارد کنید" />
              <FormInput label="قیمت تومان" name="price" type="number" value={formData.price} onChange={handleChange} placeholder="850000" />
              <FormInput label="وزن کیلوگرم" name="weight" type="number" value={formData.weight} onChange={handleChange} placeholder="0.500" />
              <FormInput label="گارانتی ماه" name="warranty" type="number" value={formData.warranty} onChange={handleChange} placeholder="12" />
              <FormInput label="برند / جنس" name="brand" value={formData.brand} onChange={handleChange} placeholder="مثلاً چوب گردو" />

              <FormInput
                label="تعداد موجودی (مجموع)"
                name="countInStock"
                type="number"
                value={totalStock}
                onChange={() => {}}
                placeholder="به‌صورت خودکار محاسبه می‌شود"
                disabled={true}
              />

              <FormInput label="وضعیت موجودی" name="status" value={formData.status} onChange={handleChange} isSelect options={[{ value: 'active', label: 'موجود' }, { value: 'almost-finished', label: 'رو به اتمام' }, { value: 'inactive', label: 'ناموجود' }]} />

              <div className="relative">
                <FormInput
                  label="دسته‌بندی اصلی"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  isSelect
                  options={categories.map(cat => ({ value: cat.id, label: cat.name }))}
                  disabled={loadingCategories}
                />
                <button
                  type="button"
                  onClick={() => navigate('/admin/categories')}
                  className="absolute left-0 bottom-0 text-xs text-blue-600 hover:text-blue-800 font-medium mt-1"
                >
                  مدیریت دسته‌بندی‌ها
                </button>
              </div>

              {/* ✅ فیلد "دسته‌بندی فرعی" حذف شد */}

              <FormInput label="توضیحات" name="description" value={formData.description} onChange={handleChange} placeholder="توضیحات محصول را وارد کنید" />

              <div className="col-span-full">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-bold text-slate-700">رنگ‌ها و موجودی هر رنگ</label>
                  <button type="button" onClick={() => setShowColorModal(true)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors">
                    <Plus className="w-4 h-4" /> افزودن رنگ جدید
                  </button>
                </div>

                <select
                  multiple
                  value={formData.color}
                  onChange={handleColorSelect}
                  className={selectClassName}
                  style={{ height: '120px' }}
                >
                  <option value="">انتخاب کنید</option>
                  {colorsList.map((color) => (
                    <option key={color.id} value={color.name}>
                      {color.name} ({color.code})
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-400">
                  برای انتخاب چند رنگ، کلید Ctrl (ویندوز) یا Cmd (مک) را نگه دارید.
                </p>

                {Object.keys(colorStocks).length > 0 && (
                  <div className="mt-4 p-4 bg-blue-50/50 rounded-xl border border-blue-100">
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-sm font-bold text-gray-700">موجودی هر رنگ</span>
                      <span className="text-sm text-gray-500">
                        مجموع: <span className="font-bold text-blue-600">{totalStock}</span>
                      </span>
                    </div>
                    <div className="space-y-3">
                      {formData.color.map((colorName) => (
                        <ColorStockInput
                          key={colorName}
                          colorName={colorName}
                          colorCode={colorsList.find(c => c.name === colorName)?.code}
                          stock={colorStocks[colorName] || 0}
                          onStockChange={(value) => handleStockChange(colorName, value)}
                          onRemove={() => handleRemoveColor(colorName)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {loadingColors && (
                  <div className="flex items-center gap-2 mt-2 text-sm text-gray-400">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    در حال دریافت رنگ‌ها...
                  </div>
                )}
                {!loadingColors && colorsList.length === 0 && (
                  <div className="mt-2 text-sm text-yellow-600">
                    ⚠️ هیچ رنگی ثبت نشده است. لطفاً از دکمه "افزودن رنگ جدید" استفاده کنید.
                  </div>
                )}
              </div>

              {/* 🛒 خرید عمده */}
              <div className="col-span-full mt-4 pt-4 border-t border-gray-200">
                <div className="flex items-center gap-4 mb-4">
                  <label className="text-sm font-bold text-slate-700">خرید عمده</label>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, wholesale_enabled: !prev.wholesale_enabled }))}
                    className={`relative w-14 h-8 rounded-full transition-colors duration-300 ${
                      formData.wholesale_enabled ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`absolute top-1 left-1 w-6 h-6 rounded-full bg-white shadow-md transition-transform duration-300 ${
                        formData.wholesale_enabled ? 'translate-x-6' : ''
                      }`}
                    />
                  </button>
                  <span className="text-sm text-gray-500">
                    {formData.wholesale_enabled ? 'فعال' : 'غیرفعال'}
                  </span>
                </div>

                {formData.wholesale_enabled && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-blue-50/50 rounded-xl border border-blue-100">
                    <FormInput
                      label="حداقل تعداد برای خرید عمده"
                      name="wholesale_min_quantity"
                      type="number"
                      value={formData.wholesale_min_quantity}
                      onChange={handleChange}
                      placeholder="مثال: ۵"
                    />
                    <FormInput
                      label="قیمت عمده (تومان)"
                      name="wholesale_price"
                      type="number"
                      value={formData.wholesale_price}
                      onChange={handleChange}
                      placeholder="مثال: ۵۰۰۰۰۰"
                    />
                    <div className="md:col-span-2 text-xs text-gray-400">
                      ⚠️ قیمت عمده باید از قیمت اصلی محصول کمتر باشد.
                    </div>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* بخش ابعاد */}
          <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm md:p-7">
            <SectionTitle icon={Ruler}>ابعاد محصول</SectionTitle>
            <p className="mb-5 text-xs text-slate-400">اندازه‌ها را بر اساس سانتی‌متر وارد کنید.</p>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
              <FormInput label="عرض" name="width" value={formData.width} onChange={handleChange} placeholder="20" />
              <FormInput label="طول" name="length" value={formData.length} onChange={handleChange} placeholder="30" />
              <FormInput label="ارتفاع" name="height" value={formData.height} onChange={handleChange} placeholder="10" />
            </div>
          </section>

          {/* دکمه‌های اقدام */}
          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <button type="button" className="rounded-xl border border-slate-200 bg-white px-7 py-3 font-bold text-slate-600 transition hover:border-slate-300 hover:bg-slate-50" onClick={() => navigate('/admin')}>انصراف</button>
            <button type="submit" disabled={isSubmitting} className="flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-8 py-3 font-bold text-white shadow-lg shadow-blue-200 transition hover:-translate-y-0.5 hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60">
              {isSubmitting ? (
                <><span className="h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" /> در حال به‌روزرسانی...</>
              ) : (
                <><Save className="h-5 w-5" /> به‌روزرسانی محصول</>
              )}
            </button>
          </div>
        </form>

        {/* مودال افزودن رنگ */}
        {showColorModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-800">افزودن رنگ جدید</h2>
                <button onClick={() => { setShowColorModal(false); setColorModalError(''); setNewColorName(''); setNewColorCode(''); }} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"><X className="w-5 h-5 text-gray-500" /></button>
              </div>
              {colorModalError && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">{colorModalError}</div>}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1">نام رنگ</label>
                  <input type="text" value={newColorName} onChange={(e) => setNewColorName(e.target.value)} placeholder="مثلاً قهوه‌ای" className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1">کد رنگ (HEX)</label>
                  <input type="text" value={newColorCode} onChange={(e) => setNewColorCode(e.target.value)} placeholder="مثلاً #2C1810" className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-xs text-gray-500">پیش‌نمایش:</span>
                    <span className="w-6 h-6 rounded-full border border-gray-300" style={{ backgroundColor: newColorCode || '#ffffff' }} />
                  </div>
                </div>
              </div>
              <div className="mt-6 flex gap-3 justify-end">
                <button onClick={() => { setShowColorModal(false); setColorModalError(''); setNewColorName(''); setNewColorCode(''); }} className="px-4 py-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors">انصراف</button>
                <button onClick={handleAddColor} disabled={submittingColor} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50">
                  {submittingColor ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  {submittingColor ? 'در حال ثبت...' : 'افزودن رنگ'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* لایت‌باکس */}
        {lightboxImage && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-4" onClick={closeLightbox}>
            <button type="button" onClick={closeLightbox} className="absolute right-6 top-6 rounded-full bg-white/20 p-3 text-white transition hover:bg-white/40"><X className="h-6 w-6" /></button>
            <div className="relative max-h-[90vh] max-w-[90vw]" onClick={(e) => e.stopPropagation()}>
              <img src={lightboxImage} alt="تصویر بزرگ شده" className="max-h-[90vh] max-w-[90vw] rounded-2xl object-contain shadow-2xl" />
            </div>
          </div>
        )}
      </div>
    </main>
  );
};

export default EditProductPage;